import React, { useCallback } from 'react';
import { useRequestPayment } from '@features/payments/hooks/useRequestPayment';
import { usePatientPaymentMethods } from '@features/payments/store/hooks';
import { Payment as PaymentIcon } from '@mui/icons-material';
import { useEventTracking } from '@services/statsig';

import { ToolbarButton } from '../components/ToolbarButton';
import { useToolbarAction } from '../store/hooks';
import { type ToolbarActionProps } from '../types';

export const RequestPaymentToolbarAction: React.FC<ToolbarActionProps> = ({ context, compact }) => {
  const { badgeCount } = useToolbarAction('request-payment');
  const { toolbar } = useEventTracking();

  // Check if patient has payment methods on file
  const { paymentMethods } = usePatientPaymentMethods(context?.patientId ?? '');
  const hasPaymentMethod = paymentMethods.length > 0;
  // Find the default payment method, or fall back to the first one
  const defaultPaymentMethod = paymentMethods.find(pm => pm.is_default) ?? paymentMethods[0];

  // Use the headless payment request hook
  const { triggerPaymentRequest } = useRequestPayment({
    patientId: context?.patientId,
    encounterId: context?.encounterId,
    hasPaymentMethod,
    defaultPaymentMethod,
    paymentMethods,
    source: 'toolbar',
  });

  const handleClick = useCallback(() => {
    // Track toolbar action click
    toolbar.actionClicked({
      action_type: hasPaymentMethod ? 'charge_payment' : 'request_payment',
      has_patient: !!context?.patientId,
    });
    // Trigger the payment request flow
    triggerPaymentRequest();
  }, [toolbar, context?.patientId, hasPaymentMethod, triggerPaymentRequest]);

  // Tooltip and label change based on whether patient has payment method on file
  const tooltipTitle = !context?.patientId
    ? 'Request payment (select patient in modal)'
    : hasPaymentMethod
      ? 'Charge payment method on file'
      : 'Request payment from patient';

  return (
    <ToolbarButton
      icon={PaymentIcon}
      label={hasPaymentMethod ? 'Charge' : 'Payment'}
      tooltip={tooltipTitle}
      onClick={handleClick}
      compact={compact}
      badgeCount={badgeCount}
      dataAttribute="toolbar-request-payment"
    />
  );
};
